/**
 * Identify the synthetic activity rows written by staff ticket tools.
 *
 * Their text is stored beside real messages, so visual prefixes alone are
 * unsafe: tenants and staff may legitimately begin a message with a
 * checkmark. The internal marker, staff scope, and delivered-at stamp must
 * all agree before the conversation renders a centered system event.
 */
export function encodeSupportSystemEvent(value: string): string {
  return value.trim();
}

const STRUCTURED_EVENT_PREFIX = "SUPPORT_EVENT_JSON:";

export type StructuredSupportEvent =
  | { type: "assignment.unassigned"; actor: string }
  | { type: "assignment.taken"; actor: string }
  | { type: "assignment.handover"; actor: string; assignee: string }
  | { type: "conversation.reopenedByReply" };

/** Store event meaning instead of whichever language the acting admin uses. */
export function encodeStructuredSupportEvent(
  event: StructuredSupportEvent
): string {
  return `${STRUCTURED_EVENT_PREFIX}${JSON.stringify({ v: 1, ...event })}`;
}

function decodeStructuredSupportEvent(
  value: string
): (StructuredSupportEvent & { v: number }) | null {
  if (!value.startsWith(STRUCTURED_EVENT_PREFIX)) return null;
  try {
    const parsed = JSON.parse(value.slice(STRUCTURED_EVENT_PREFIX.length)) as
      (StructuredSupportEvent & { v?: unknown }) | null;
    if (!parsed || parsed.v !== 1 || typeof parsed.type !== "string")
      return null;
    return parsed as StructuredSupportEvent & { v: number };
  } catch {
    return null;
  }
}

export function supportSystemEventText(
  value: string,
  senderScope: "tenant" | "admin" | null | undefined,
  deliveredAt: string | null | undefined,
  isSystem?: boolean | null,
  t: (key: string, fallback?: string) => string = (_key, fallback = "") =>
    fallback
): string | null {
  void deliveredAt;
  if (!isSystem || senderScope !== "admin") return null;
  const event = decodeStructuredSupportEvent(value);
  if (!event) return value.trim() || null;
  if (event.type === "conversation.reopenedByReply") {
    return t(
      "support.event.reopenedByReply",
      "Conversation reopened by a new customer reply"
    );
  }
  if (event.type === "assignment.unassigned") {
    return t(
      "support.event.unassigned",
      "{actor} unassigned the conversation"
    ).replace("{actor}", event.actor);
  }
  if (event.type === "assignment.taken") {
    return t(
      "support.event.taken",
      "{actor} took over the conversation"
    ).replace("{actor}", event.actor);
  }
  return t(
    "support.event.handover",
    "{actor} handed over to {assignee} · {assignee} notified"
  )
    .replace("{actor}", event.actor)
    .replaceAll("{assignee}", event.assignee);
}
